import {useEffect, useState} from "react";
import {useFormik} from "formik";
import {useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    Typography,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Switch,
    Autocomplete,
    Avatar,
    InputAdornment
} from "@mui/material";
import {
    Close as CloseIcon,
    PersonAdd as PersonAddIcon,
    Search as SearchIcon,
    Visibility,
    VisibilityOff
} from "@mui/icons-material";
import {RTL} from "../cache/CacheRtl";
import {useCompany} from "../contexts/CompanyContext";
import {useAuth} from "../contexts/AuthContext";
import {createTrainer, promotToTrainer, updateTrainer} from "../services/TrainerService";
import {useMessageContext} from "../contexts/MessageContext";
import {getErrorMessage} from "../enums/ErrorMessages";
import {getUsersWithoutRoles} from "../services/UserService";
import {filterUserCandidates, promotableCandidates} from "../utils/userCandidates";
import PhoneInput from "./shared/PhoneInput";
import {colors, borders} from "./ui/colors";

// The matching rule moved to utils/userCandidates so the ADMIN-granting picker in AddOwnerDialog
// ("מנהל חדש" and the group-admin flows) uses the same one. Re-exported rather than re-pointed at
// the callers, because this name is this file's public surface and NewTrainerDialog.filter.test.js
// imports it from here.
export {filterUserCandidates};

const NewTrainerDialog = ({open, close, trainer, reload}) => {
    const {getCompaniesList, company} = useCompany();
    const {isSuperAdmin} = useAuth();
    const navigate = useNavigate();
    const {notificationHandler} = useMessageContext();

    const [existingUser, setExistingUser] = useState(!trainer);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);

    useEffect(() => {
        if (open) {
            getUsersWithoutRoles("TRAINER", company?.id).then(res => {
                // The server already drops disabled users from this pool; re-applying it here keeps
                // the two pickers on one rule and covers a stale deployment.
                setAvailableUsers(promotableCandidates(res.data));
            }).catch(e => {
                // Never swallow this silently: an empty picker is indistinguishable from "no
                // candidates exist", which is exactly how the 403 on the old /system endpoint hid
                // itself — users read "לא נמצאו משתמשים" as a broken search.
                setAvailableUsers([]);
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            });
        }
    }, [company, open]);

    const formik = useFormik({
        initialValues: {
            name: trainer?.name || "",
            phoneNumber: trainer?.phoneNumber || "",
            email: trainer?.email || "",
            gender: trainer?.gender || "null",
            companyId: trainer?.companyId || "null",
            id: trainer?.id || null,
            userId: "null",
            password: "",
            secondPassword: ""
        },
        enableReinitialize: true,
        validate: values => {
            const errors = {};
            if (!trainer && !existingUser && !values.password) {
                errors.password = "שדה חובה";
            }
            if (values.password !== values.secondPassword) {
                errors.secondPassword = "סיסמאות לא תואמות";
            }
            if (!existingUser && !values.name) {
                errors.name = "שדה חובה";
            }
            if (!existingUser && !values.email) {
                errors.email = "שדה חובה";
            }
            return errors;
        },
        onSubmit: (values) => {
            const body = {...values};
            body.gender = body.gender === "null" ? null : body.gender;
            body.userId = body.userId === "null" ? null : body.userId;
            body.companyId = body.companyId === "null" ? null : body.companyId;
            delete body.secondPassword;
            body.password = body.password === "" ? null : body.password;

            if (existingUser) {
                promotToTrainer(body.userId)
                    .then((res) => {
                        notificationHandler.success("מאמן עודכן בהצלחה");
                        navigate(`/app/trainers/${res.data.id}`);
                    }).catch(e => {
                    notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
                });
            } else if (!trainer) {
                createTrainer(body)
                    .then((res) => {
                        notificationHandler.success("מאמן נוצר בהצלחה");
                        navigate(`/app/trainers/${res.data.id}`);
                    }).catch(e => {
                    notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
                });
            } else {
                updateTrainer(body)
                    .then(() => notificationHandler.success("מאמן התעדכן בהצלחה"))
                    .then(() => reload(prv => !prv))
                    .then(handleClose)
                    .catch(e => {
                        notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
                    });
            }
        }
    });

    const handleClose = () => {
        formik.resetForm();
        setExistingUser(!trainer);
        setShowPassword(false);
        setShowPassword2(false);
        close();
    };

    const isSubmitDisabled = () => {
        if (existingUser) return formik.values.userId === "null";
        return !formik.values.name || !formik.values.email;
    };

    const selectedUser = availableUsers.find(u => u.id === formik.values.userId) || null;

    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: borders.radius.md,
            backgroundColor: colors.white,
            "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary},
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: "2px"},
        },
        "& .MuiOutlinedInput-notchedOutline": {borderColor: colors.border},
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "480px"},
                    maxWidth: "540px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <PersonAddIcon sx={{color: colors.textSecondary, fontSize: "20px"}}/>
                            <Typography sx={{fontWeight: 600, fontSize: "16px", color: colors.textPrimary}}>
                                {trainer ? "עריכת מאמן" : "הוספת מאמן"}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: colors.textMuted,
                                "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary}
                            }}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        <form onSubmit={formik.handleSubmit} id="trainer-form">

                            {/* Existing user toggle — only for new trainers */}
                            {!trainer && (
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "10px 14px",
                                    backgroundColor: colors.bg,
                                    border: borders.default,
                                    borderRadius: borders.radius.md,
                                    marginBottom: "20px"
                                }}>
                                    <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary}}>
                                        קידום משתמש קיים למאמן
                                    </Typography>
                                    <Switch
                                        checked={existingUser}
                                        onChange={() => {
                                            setExistingUser(prv => {
                                                formik.setFieldValue("userId", "null");
                                                return !prv;
                                            });
                                        }}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": {color: colors.primary},
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: colors.primary}
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Existing user selector */}
                            {existingUser && !trainer && (
                                <Box sx={{marginBottom: "20px"}}>
                                    <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                        בחר משתמש
                                    </Typography>
                                    <Autocomplete
                                        value={selectedUser}
                                        disableClearable={false}
                                        getOptionLabel={(option) => option?.name || ""}
                                        filterOptions={(options, {inputValue}) =>
                                            filterUserCandidates(options, inputValue)
                                        }
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        onChange={(_, value) => formik.setFieldValue("userId", value?.id || "null")}
                                        options={availableUsers}
                                        noOptionsText="לא נמצאו משתמשים"
                                        sx={{
                                            ...fieldSx,
                                            "& .MuiAutocomplete-popupIndicator": {color: colors.textMuted},
                                        }}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                padding: "8px 12px !important"
                                            }}>
                                                <Avatar sx={{
                                                    width: 28, height: 28,
                                                    backgroundColor: colors.primaryLight,
                                                    fontSize: "12px",
                                                    color: colors.primary
                                                }}>
                                                    {option.name?.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{fontSize: "14px", fontWeight: 500, color: colors.textPrimary}}>
                                                        {option.name}
                                                    </Typography>
                                                    {option.email && (
                                                        <Typography sx={{fontSize: "12px", color: colors.textMuted}}>
                                                            {option.email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="חפש משתמש..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <SearchIcon sx={{color: colors.textMuted, fontSize: 20, mr: 0.5}}/>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    )
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            )}

                            {/* New user form */}
                            {!existingUser && (
                                <>
                                    {/* Name + Email row */}
                                    <Box sx={{display: "flex", gap: 2, mb: 2}}>
                                        <Box sx={{flex: 1}}>
                                            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                שם מלא *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                id="name"
                                                value={formik.values.name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.name && !!formik.errors.name}
                                                helperText={formik.touched.name && formik.errors.name}
                                                sx={fieldSx}
                                            />
                                        </Box>
                                        <Box sx={{flex: 1}}>
                                            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                אימייל *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                id="email"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.email && !!formik.errors.email}
                                                helperText={formik.touched.email && formik.errors.email}
                                                sx={fieldSx}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Phone + Gender row */}
                                    <Box sx={{display: "flex", gap: 2, mb: 2}}>
                                        <Box sx={{flex: 1}}>
                                            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                טלפון
                                            </Typography>
                                            <PhoneInput
                                                value={formik.values.phoneNumber}
                                                onChange={(value) => formik.setFieldValue("phoneNumber", value)}
                                            />
                                        </Box>
                                        <Box sx={{flex: 1}}>
                                            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                מגדר
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={formik.values.gender}
                                                    onChange={formik.handleChange("gender")}
                                                    sx={{
                                                        borderRadius: borders.radius.md,
                                                        "& .MuiOutlinedInput-notchedOutline": {borderColor: colors.border},
                                                        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary},
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: "2px"},
                                                    }}
                                                >
                                                    <MenuItem value="null">לא נבחר</MenuItem>
                                                    <MenuItem value="MALE">זכר</MenuItem>
                                                    <MenuItem value="FEMALE">נקבה</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>

                                    {/* Password row */}
                                    {!trainer && (
                                        <Box sx={{display: "flex", gap: 2, mb: 2}}>
                                            <Box sx={{flex: 1}}>
                                                <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                    סיסמה *
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    id="password"
                                                    type={showPassword ? "text" : "password"}
                                                    value={formik.values.password}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.password && !!formik.errors.password}
                                                    helperText={formik.touched.password && formik.errors.password}
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end" tabIndex={-1}>
                                                                    {showPassword ? <VisibilityOff sx={{fontSize: 18, color: colors.textMuted}}/> : <Visibility sx={{fontSize: 18, color: colors.textMuted}}/>}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{flex: 1}}>
                                                <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                    סיסמה בשנית *
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    id="secondPassword"
                                                    type={showPassword2 ? "text" : "password"}
                                                    value={formik.values.secondPassword}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.secondPassword && !!formik.errors.secondPassword}
                                                    helperText={formik.touched.secondPassword && formik.errors.secondPassword}
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton size="small" onClick={() => setShowPassword2(p => !p)} edge="end" tabIndex={-1}>
                                                                    {showPassword2 ? <VisibilityOff sx={{fontSize: 18, color: colors.textMuted}}/> : <Visibility sx={{fontSize: 18, color: colors.textMuted}}/>}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Company selector — super admin only */}
                                    {isSuperAdmin() && (
                                        <Box sx={{mb: 2}}>
                                            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 1}}>
                                                חברה
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={formik.values.companyId}
                                                    onChange={formik.handleChange("companyId")}
                                                    sx={{
                                                        borderRadius: borders.radius.md,
                                                        "& .MuiOutlinedInput-notchedOutline": {borderColor: colors.border},
                                                        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary},
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: "2px"},
                                                    }}
                                                >
                                                    <MenuItem value="null">לא נבחר</MenuItem>
                                                    {getCompaniesList().map((c) => (
                                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}
                                </>
                            )}
                        </form>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: 1.5,
                        padding: "16px 20px",
                        borderTop: borders.default,
                        backgroundColor: colors.bg
                    }}>
                        <Button
                            type="submit"
                            form="trainer-form"
                            disabled={isSubmitDisabled()}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.primary,
                                color: colors.white,
                                fontSize: "13px",
                                fontWeight: 600,
                                padding: "8px 24px",
                                borderRadius: borders.radius.md,
                                textTransform: "none",
                                "&:hover": {backgroundColor: colors.primaryDark},
                                "&.Mui-disabled": {backgroundColor: colors.border, color: colors.textMuted}
                            }}
                        >
                            {trainer ? "עדכן" : "שמור"}
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                borderColor: colors.border,
                                color: colors.textSecondary,
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "8px 20px",
                                borderRadius: borders.radius.md,
                                textTransform: "none",
                                "&:hover": {borderColor: colors.textSecondary, backgroundColor: colors.bgHover}
                            }}
                        >
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default NewTrainerDialog;
